import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'
import {
  isPossiblePhoneNumber,
  parsePhoneNumberWithError,
} from 'libphonenumber-js'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useMangoApi } from '~/atoms/MangoApi'
import { useSettingsState } from '~/atoms/SettingsState'
import { QueryCancellation, UseQueryParameters } from '~/types/ApiRequestTypes'
import { getPaginatedResultParts } from '~/utils/apiQueryUtils'

import {
  Call,
  CallInstance,
  CallLog,
  CallsQueryParams,
  ExtensionPreview,
  PaginatedApiResult,
} from '@mangovoice/types'
import { ApiError } from '@mangovoice/types'

const DEFAULT_CALLS_LIMIT = 25

export const transformCallLogExtensionCallerIds = (
  callLogs: CallLog[],
  extensions: ExtensionPreview[],
): CallLog[] => {
  const extensionMap = new Map<string, string>(
    extensions.map((ext) => [ext.number, ext.name]),
  )

  return callLogs.map((callLog) => {
    const transformedLog = { ...callLog }

    if (transformedLog.to_extension) {
      transformedLog.to_phone_number = {
        ...transformedLog.to_phone_number,
        caller_id:
          extensionMap.get(transformedLog.to_extension.number) ??
          transformedLog.to_phone_number?.caller_id,
      }
    }

    if (transformedLog.from_extension) {
      transformedLog.from_phone_number = {
        ...transformedLog.from_phone_number,
        caller_id:
          extensionMap.get(transformedLog.from_extension.number) ??
          transformedLog.from_phone_number?.caller_id,
      }
    }

    return transformedLog
  })
}

export const useGetRecentCallsWithExtensionCallerIds = (
  pbxId: number,
  options: Record<string, unknown> = {},
) => {
  const mangoApi = useMangoApi()

  return useQuery({
    queryKey: ['recentCallsWithExtensionCallerIds', pbxId],
    queryFn: async () => {
      if (pbxId === 0) return Promise.reject(undefined)

      const { ok: extensionResultOk, data: extensionData } =
        await mangoApi.getPbxExtensions(pbxId)

      if (!extensionResultOk) return Promise.reject(extensionData as ApiError)

      const { ok: callResultOk, data: callData } =
        await mangoApi.getRecentCalls(pbxId)
      if (!callResultOk || !callData)
        return Promise.reject(callData as ApiError)

      const transformedCallLogs = transformCallLogExtensionCallerIds(
        (callData as PaginatedApiResult<CallLog>).results,
        (extensionData as PaginatedApiResult<ExtensionPreview>).results,
      )

      return transformedCallLogs
    },
    ...options,
    retry: (options.retry as number | boolean | undefined) ?? false,
  })
}

export const useGetPbxExtensions = (
  pbxId: number,
  options: Record<string, unknown> = {},
) => {
  const mangoApi = useMangoApi()

  return useQuery({
    queryKey: ['pbxExtensions', pbxId],
    queryFn: async () => {
      if (pbxId === 0) return Promise.resolve(undefined)

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const { ok, data } = await mangoApi.getPbxExtensions(pbxId)
      if (!ok) return Promise.reject(data as ApiError)

      return data
    },
    ...options,
    retry: (options.retry as number | boolean | undefined) ?? false,
  })
}

export const useGetRecentCalls = (
  pbxId: number,
  options: Record<string, unknown> = {},
) => {
  const mangoApi = useMangoApi()

  return useQuery({
    queryKey: ['recentCalls', pbxId],
    queryFn: async () => {
      if (pbxId === 0) return Promise.reject(undefined)

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      const { data } = await mangoApi.getRecentCalls(pbxId)
      return data as PaginatedApiResult<CallLog>
    },
    ...options,
    retry: (options.retry as number | boolean | undefined) ?? false,
  })
}

export const useGetInfiniteCalls = (
  pbxId: number,
  queryParams: CallsQueryParams,
  options?: any,
) => {
  const api = useMangoApi()
  const queryClient = useQueryClient()
  const queryKey = useMemo(
    () => ['infinite-calls', { ...queryParams, pbx_ids: [pbxId] }],
    [queryParams, pbxId],
  )

  const infiniteQuery = useInfiniteQuery({
    queryKey,
    queryFn: async ({ signal, pageParam = 0 }) => {
      if (pbxId === 0) {
        return Promise.reject({ message: 'pbxId is required' })
      }

      const params = {
        ...queryParams,
        pbx_ids: [pbxId],
        limit: DEFAULT_CALLS_LIMIT,
        offset: pageParam,
      } as CallsQueryParams

      const { ok, data, status } = await api.getCalls(params, signal)

      if (!ok) {
        const error = { ...data, status }
        return Promise.reject(error)
      }

      return data!
    },
    getNextPageParam: (lastPage) => {
      const parts = getPaginatedResultParts(lastPage)
      return parts?.hasNextPage ? parts.nextPageOffset : undefined
    },
    initialPageParam: 0,
    ...options,
  })

  const refetch = useCallback(() => {
    infiniteQuery.refetch()
  }, [infiniteQuery])

  const results = useMemo(() => {
    return infiniteQuery.data?.pages.flatMap((page) => page.results) || []
  }, [infiniteQuery.data])

  const totalCount = useMemo(() => {
    return infiniteQuery.data?.pages[0].count
  }, [infiniteQuery.data])

  // Cache utility for updating a call in the cache
  const updateCache = useCallback(
    (updatedCall: Call) => {
      queryClient.setQueryData(queryKey, (oldData: any) => {
        if (!oldData) return undefined
        return {
          ...oldData,
          pages: oldData.pages.map((page: any) => ({
            ...page,
            results: page.results.map((call: Call) =>
              call.id === updatedCall.id ? updatedCall : call,
            ),
          })),
        }
      })
    },
    [queryKey, queryClient],
  )

  return {
    ...infiniteQuery,
    results,
    refetch,
    totalCount,
    updateCache,
  }
}

type UseGetCallParams = UseQueryParameters<CallInstance>

export const useGetCall = ({
  onQueryStarted,
  onQuerySuccess,
  onQueryError,
  onQueryCancelled,
  options,
}: UseGetCallParams) => {
  const mangoApi = useMangoApi()
  const queryClient = useQueryClient()
  const [queryParams, setQueryParams] = useState({
    callId: 0,
  })

  const getQueryKey = () => {
    const key = ['call', queryParams.callId]
    return key
  }

  const tryCancelQuery = () => {
    const queryKey = getQueryKey()
    queryClient.cancelQueries({ queryKey })
  }

  const refetch = (newCallId: number) => {
    tryCancelQuery()

    setQueryParams({
      callId: newCallId,
    })
  }

  const { callId } = queryParams
  const query = useQuery<CallInstance, ApiError>({
    queryKey: getQueryKey(),
    queryFn: async ({ signal }) => {
      onQueryStarted?.()
      if (callId === 0) return Promise.reject(undefined)
      const { ok, data, status } = await mangoApi.getCall(callId, signal)

      if (!ok) {
        const errorData = {
          status,
          ...(data as ApiError),
        }

        if (signal.aborted) {
          onQueryCancelled?.()
          return Promise.reject(errorData)
        }

        onQueryError?.(errorData)
        return Promise.reject(errorData)
      }

      // This call doesn't match the existing call response. It should have transcript_id, but instead has a transcript.id.
      // Lets fix the model before moving forward here.
      const callData = data as CallInstance

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const fixedTranscriptId = (callData.transcript as any)?.id
      if (fixedTranscriptId && !callData.transcript_id) {
        callData.transcript_id = fixedTranscriptId
      }

      onQuerySuccess?.(callData)
      return callData
    },
    ...options,
    enabled: options?.enabled ?? false,
    retry: (options?.retry as number | boolean | undefined) ?? false,
  })

  useEffect(() => {
    if (callId !== 0) {
      query.refetch()
    }
  }, [queryParams])

  return {
    ...query,
    refetch: refetch,
  }
}

export const useCall = (
  callId: number,
  options?: Omit<UseQueryOptions<CallInstance, ApiError>, 'queryKey'>,
) => {
  const api = useMangoApi()
  const queryClient = useQueryClient()

  const getQueryKeys = () => [`calls`, callId]

  const tryCancelQueries: QueryCancellation = () => {
    queryClient.cancelQueries({
      queryKey: getQueryKeys(),
    })
  }

  const query = useQuery<CallInstance, ApiError>({
    queryKey: getQueryKeys(),
    queryFn: async ({ signal }) => {
      if (!callId || callId === 0) {
        return Promise.reject({ message: 'callId is required' })
      }
      const { ok, data, status } = await api.getCall(callId, signal)

      if (!ok) {
        const error = {
          ...data,
          status,
        }

        return Promise.reject(error)
      }

      return data!
    },
    ...options,
  })

  return {
    ...query,
    tryCancelQueries,
  }
}

export const usePostClickToCall = ({
  extensionId,
}: {
  extensionId: number
}) => {
  const api = useMangoApi()
  const {
    settings: {
      clickToCall: { shouldAutoAnswer },
    },
  } = useSettingsState()
  return useMutation({
    mutationFn: async ({ phoneNumber }: { phoneNumber: string }) => {
      if (!extensionId) return Promise.reject('No extension id provided.')

      const parsed = parsePhoneNumberWithError(phoneNumber, {
        defaultCountry: 'US',
      })

      if (!isPossiblePhoneNumber(parsed.number))
        return Promise.reject('Invalid phone number.')

      const resultNumber = isPossiblePhoneNumber(parsed.number)
        ? parsed.format('E.164')
        : phoneNumber

      const { ok } = await api.postClickToCall({
        extensionId,
        phoneNumber: resultNumber,
        autoAnswer: shouldAutoAnswer,
      })

      if (!ok) return Promise.reject('Failed to initiate click to call.')
      return Promise.resolve()
    },
  })
}
